import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, HOME_FOR_ROLE } from '../../context/AuthContext';
import { Button, Field } from '../../components/ui';
import { useToast } from '../../components/Toast';
import OtpVerify from '../../components/OtpVerify';
import BrandMark from '../../components/BrandMark';

export default function Register({ role }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, registerNanny, registerFamily } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', familyName: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Once registration accepts the form, we stop showing it and ask for the
  // code we just emailed instead — the account isn't created "for real"
  // (signed in) until that code is confirmed.
  const [pendingEmail, setPendingEmail] = useState(null);

  if (user) return <Navigate to={HOME_FOR_ROLE[user.role]} replace />;

  const copy = role === 'nanny'
    ? {
      title: t('auth.register.nannyTitle'),
      lede: t('auth.register.nannyLede'),
      cta: t('auth.register.nannyCta'),
      alt: { text: t('auth.register.nannyAltText'), to: '/register/family', label: t('auth.register.nannyAltLabel') },
    }
    : {
      title: t('auth.register.familyTitle'),
      lede: t('auth.register.familyLede'),
      cta: t('auth.register.familyCta'),
      alt: { text: t('auth.register.familyAltText'), to: '/register/nanny', label: t('auth.register.familyAltLabel') },
    };
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function onSubmit(event) {
    event.preventDefault();
    setFieldErrors({});

    if (form.password !== form.confirmPassword) {
      setFieldErrors({ confirmPassword: t('auth.register.passwordMismatch') });
      return;
    }

    setBusy(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        ...(role === 'family' ? { familyName: form.familyName } : {}),
      };
      const result = role === 'nanny' ? await registerNanny(payload) : await registerFamily(payload);
      setPendingEmail(result.email);
    } catch (err) {
      toast.error(err.message);
      setFieldErrors(err.fieldErrors || {});
    } finally {
      setBusy(false);
    }
  }

  function onVerified(me) {
    navigate(role === 'nanny' ? '/nanny/application' : HOME_FOR_ROLE[me.role], { replace: true });
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <Link to="/" className="auth-brand">
          <BrandMark />
          <strong>Nannies to the Rescue</strong>
        </Link>

        {pendingEmail ? (
          <OtpVerify email={pendingEmail} onVerified={onVerified} />
        ) : (
          <>
            <h1>{copy.title}</h1>
            <p className="muted">{copy.lede}</p>

            <form onSubmit={onSubmit} noValidate>
              <div className="grid-2">
                <Field label={t('auth.register.firstName')} required error={fieldErrors.firstName}>
                  <input value={form.firstName} onChange={set('firstName')} autoComplete="given-name" required />
                </Field>
                <Field label={t('auth.register.lastName')} required error={fieldErrors.lastName}>
                  <input value={form.lastName} onChange={set('lastName')} autoComplete="family-name" required />
                </Field>
              </div>

              {role === 'family' && (
                <Field label={t('auth.register.familyName')} hint={t('auth.register.familyNameHint')}>
                  <input value={form.familyName} onChange={set('familyName')} />
                </Field>
              )}

              <div className="grid-2">
                <Field label={t('auth.register.email')} required error={fieldErrors.email}>
                  <input type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
                </Field>
                <Field label={t('auth.register.phone')} error={fieldErrors.phone}>
                  <input value={form.phone} onChange={set('phone')} autoComplete="tel" />
                </Field>
              </div>

              <div className="grid-2">
                <Field label={t('auth.register.password')} required hint={t('auth.register.passwordHint')} error={fieldErrors.password}>
                  <input
                    type="password"
                    value={form.password}
                    onChange={set('password')}
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </Field>
                <Field label={t('auth.register.confirmPassword')} required error={fieldErrors.confirmPassword}>
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={set('confirmPassword')}
                    autoComplete="new-password"
                    required
                  />
                </Field>
              </div>

              <Button type="submit" loading={busy}>{copy.cta}</Button>
            </form>

            <p className="auth-alt">
              {t('auth.register.alreadyHaveAccount')} <Link to="/login">{t('auth.register.signIn')}</Link>.<br />
              {copy.alt.text} <Link to={copy.alt.to}>{copy.alt.label}</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
